// User Types
export enum UserRole {
  ADMIN = 'admin',
  MANAGER = 'manager',
  USER = 'user'
}

export interface User {
  id: string;
  username: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  role: string;
  is_active: boolean;
  created_at: string;
  last_login?: string;
}

export interface UserCreate {
  username: string;
  email?: string;
  password?: string;
  role: string;
}

// Entity Types
export enum EntityStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PROCESSING = 'processing',
  PENDING = 'pending',
  TRAINED = 'trained',
  FAILED = 'failed'
}

export interface Entity {
  id: string;
  name: string;
  description?: string;
  status: EntityStatus;
  created_at: string;
  updated_at: string;
  user_id: string;
  is_processed: boolean;
  file_count: number;
  url_count: number;
}

export interface EntityCreate {
  name: string;
  description?: string;
  user_id: string;
}

export interface EntityUpdate {
  name?: string;
  description?: string;
  status?: EntityStatus;
}

// Chat Types
export interface ChatSession {
  id: string;
  entity_id: string;
  user_id?: string;
  session_token: string;
  created_at: string;
  last_activity: string;
  is_active: boolean;
}

export interface ChatRequest {
  message: string;
  entity_id: string;
  session_token?: string;
  include_context: boolean;
}

export interface ChatResponse {
  response: string;
  entity_id: string;
  entity_name: string;
  relevant_documents: Record<string, any>[];
  session_token?: string;
  confidence_score?: number;
}

// System Types
export interface SystemStats {
  total_users: number;
  active_users: number;
  total_entities: number;
  active_entities: number;
  total_files: number;
  total_urls: number;
  system_health: string;
  last_updated: string;
}

export interface SystemHealth {
  status: string;
  uptime: string;
  response_time: string;
  error_rate: string;
  active_connections: number;
  memory_usage: string;
  cpu_usage: string;
  disk_usage: string;
  last_check: string;
}

// API Response Types
export interface ApiResponse<T> {
  status: string;
  message: string;
  data: T;
}

export interface PaginatedResponse<T> {
  status: string;
  message: string;
  data: T[];
  pagination: {
    page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
  };
}

// Auth Types
export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  user_id: string;
  username: string;
  role: string;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// State Types
export interface UserState {
  users: User[];
  isLoading: boolean;
  error: string | null;
}

export interface EntityState {
  entities: Entity[];
  isLoading: boolean;
  error: string | null;
}

export interface SystemState {
  stats: SystemStats | null;
  health: SystemHealth | null;
  isLoading: boolean;
  error: string | null;
}

// UI Types
export interface SidebarItem {
  title: string;
  path: string;
  icon: React.ComponentType;
  children?: SidebarItem[];
}

export interface TableColumn {
  field: string;
  headerName: string;
  width?: number;
  flex?: number;
  renderCell?: (params: any) => React.ReactNode;
}

export interface ChartData {
  name: string;
  value: number;
  color?: string;
}

// Toast Types
export interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
  timestamp: number;
}

export interface ToastState {
  toasts: Toast[];
}

// Topic Analysis Types
export interface TopicAnalysisRequest {
  topic: string;
}

export interface TopicAnalysisGraph {
  title: string;
  type: 'bar' | 'line' | 'pie' | 'scatter' | 'area';
  data: Array<{
    [key: string]: any;
  }>;
  description: string;
}

export interface TopicAnalysisResponse {
  topic: string;
  description: string;
  graphs: TopicAnalysisGraph[];
  generated_at: string;
}

export interface TopicAnalysisState {
  analysis: TopicAnalysisResponse | null;
  isLoading: boolean;
  error: string | null;
}